import { useState } from 'react';
import { Printer } from 'lucide-react';
import { EntityModal } from '../common';
import { DEFAULT_PRINT_OPTIONS, type FloorPlanPrintOptions } from './print';

interface FloorPlanPrintModalProps {
  open: boolean;
  onClose: () => void;
  onPrint: (options: FloorPlanPrintOptions) => void;
  /** Show the family-group coloring option (producer only — needs guest data). */
  showFamilyColorsOption?: boolean;
  /**
   * Fired as soon as the family-colors checkbox changes, so the host page can
   * turn on the on-canvas family colors before the SVG is cloned for print.
   */
  onFamilyColorsChange?: (checked: boolean) => void;
}

export function FloorPlanPrintModal({
  open,
  onClose,
  onPrint,
  showFamilyColorsOption = false,
  onFamilyColorsChange,
}: FloorPlanPrintModalProps) {
  const [options, setOptions] = useState<FloorPlanPrintOptions>({ ...DEFAULT_PRINT_OPTIONS });

  const setOption = <K extends keyof FloorPlanPrintOptions>(
    key: K,
    value: FloorPlanPrintOptions[K],
  ) => setOptions((prev) => ({ ...prev, [key]: value }));

  return (
    <EntityModal
      open={open}
      onClose={onClose}
      title="Print Options"
      size="xs"
      presentation="centered"
      footer={
        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 py-2 border border-gray-300 rounded text-sm text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              onClose();
              onPrint(options);
            }}
            className="flex-1 py-2 bg-primary-600 text-white rounded text-sm hover:bg-primary-700 flex items-center justify-center gap-1"
          >
            <Printer size={14} />
            Print
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Orientation</label>
          <div className="flex gap-2">
            <button
              onClick={() => setOption('landscape', true)}
              className={`flex-1 py-2 border rounded text-sm ${
                options.landscape
                  ? 'bg-primary-100 border-primary-300 text-primary-700 font-medium'
                  : 'border-gray-300 text-gray-600 hover:bg-gray-50'
              }`}
            >
              Landscape
            </button>
            <button
              onClick={() => setOption('landscape', false)}
              className={`flex-1 py-2 border rounded text-sm ${
                !options.landscape
                  ? 'bg-primary-100 border-primary-300 text-primary-700 font-medium'
                  : 'border-gray-300 text-gray-600 hover:bg-gray-50'
              }`}
            >
              Portrait
            </button>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Page Size</label>
          <select
            value={options.pageSize}
            onChange={(e) => setOption('pageSize', e.target.value as 'A4' | 'Letter' | 'Legal')}
            className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm text-gray-700"
          >
            <option value="A4">A4</option>
            <option value="Letter">Letter</option>
            <option value="Legal">Legal</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="print-fit-page"
            checked={options.fitPage}
            onChange={(e) => setOption('fitPage', e.target.checked)}
            className="h-4 w-4 rounded border-gray-300"
          />
          <label htmlFor="print-fit-page" className="text-sm text-gray-700">
            Fit to page (scale floor plan to fill the page)
          </label>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="print-include-info"
            checked={options.includeInfo}
            onChange={(e) => setOption('includeInfo', e.target.checked)}
            className="h-4 w-4 rounded border-gray-300"
          />
          <label htmlFor="print-include-info" className="text-sm text-gray-700">
            Include plan info (event name, date, room)
          </label>
        </div>
        {showFamilyColorsOption && (
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="print-family-colors"
              checked={options.familyColors}
              onChange={(e) => {
                setOption('familyColors', e.target.checked);
                onFamilyColorsChange?.(e.target.checked);
              }}
              className="h-4 w-4 rounded border-gray-300"
            />
            <label htmlFor="print-family-colors" className="text-sm text-gray-700">
              Color seats by family group + legend
            </label>
          </div>
        )}
      </div>
    </EntityModal>
  );
}
